import { createLogger } from '@assetron/common/telemetry/logger';

const STORAGE_KEYS = {
  correlationId: 'billingFlowCorrelationId',
  scenarioId: 'billingFlowScenarioId',
  organizationId: 'billingFlowOrganizationId',
} as const;

export const BILLING_FLOW_QUERY_PARAMS = {
  correlationId: 'billing_correlation_id',
  scenarioId: 'billing_scenario_id',
} as const;

interface BillingFlowDiagnosticsContext {
  correlationId: string;
  scenarioId?: string;
  organizationId?: string;
}

const BILLING_FLOW_EVENTS_KEY = 'assetronBillingFlowEvents';
const BILLING_FLOW_CONSOLE_PREFIX = '[assetron-diagnostics]';
const billingFlowTelemetryLogger = createLogger('billing.flow');

const isBrowser = (): boolean => globalThis.window !== undefined;

const shouldEmitBillingFlowDiagnostics = (): boolean => {
  if (!isBrowser()) {
    return false;
  }

  const sessionStorage = getWindowSessionStorage();
  const localStorage = getWindowLocalStorage();
  const searchParams = new URLSearchParams(globalThis.window.location.search);
  const storedCorrelationId =
    searchParams.get(BILLING_FLOW_QUERY_PARAMS.correlationId)?.trim() ||
    readStorage(sessionStorage, STORAGE_KEYS.correlationId) ||
    readStorage(localStorage, STORAGE_KEYS.correlationId);
  const storedScenarioId =
    searchParams.get(BILLING_FLOW_QUERY_PARAMS.scenarioId)?.trim() ||
    readStorage(sessionStorage, STORAGE_KEYS.scenarioId) ||
    readStorage(localStorage, STORAGE_KEYS.scenarioId);
  const storedOrganizationId =
    readStorage(sessionStorage, STORAGE_KEYS.organizationId) ||
    readStorage(localStorage, STORAGE_KEYS.organizationId);
  if (storedCorrelationId || storedScenarioId || storedOrganizationId) {
    return true;
  }

  const { pathname } = globalThis.window.location;
  return (
    pathname.includes('/organizations/new') ||
    pathname.includes('/subscriptions/') ||
    pathname.includes('/service-desk/') ||
    pathname.includes('/dashboard')
  );
};

const readStorage = (
  storage: Storage | undefined,
  key: string,
): string | undefined => {
  if (!storage) {
    return undefined;
  }

  return storage.getItem(key)?.trim() || undefined;
};

const writeStorage = (
  storage: Storage | undefined,
  key: string,
  value?: string,
): void => {
  if (!storage) {
    return;
  }

  if (!value) {
    storage.removeItem(key);
    return;
  }

  storage.setItem(key, value);
};

const getWindowSessionStorage = (): Storage | undefined => {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    return globalThis.window.sessionStorage;
  } catch {
    return undefined;
  }
};

const getWindowLocalStorage = (): Storage | undefined => {
  if (!isBrowser()) {
    return undefined;
  }

  try {
    return globalThis.window.localStorage;
  } catch {
    return undefined;
  }
};

const getCryptoObject = (): Crypto | undefined => {
  if (globalThis.crypto === undefined) {
    return undefined;
  }

  return globalThis.crypto;
};

const createCryptoFallbackId = (): string => {
  const cryptoObject = getCryptoObject();
  if (!cryptoObject?.getRandomValues) {
    return `billing-${Date.now()}`;
  }

  const randomBytes = new Uint8Array(16);
  cryptoObject.getRandomValues(randomBytes);
  const suffix = Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');

  return `billing-${Date.now()}-${suffix}`;
};

const generateCorrelationId = (): string => {
  const cryptoObject = getCryptoObject();
  if (cryptoObject?.randomUUID) {
    return cryptoObject.randomUUID();
  }

  return createCryptoFallbackId();
};

const summarizeUrlValue = (value: string) => {
  try {
    const parsed = new URL(
      value,
      isBrowser() ? globalThis.window.location.origin : 'https://localhost',
    );

    return {
      origin: parsed.origin,
      pathname: parsed.pathname,
    };
  } catch {
    return {
      origin: null,
      pathname: null,
    };
  }
};

const sanitizePayloadValue = (key: string, value: unknown): unknown => {
  if (value == null) {
    return value;
  }

  const normalizedKey = key.toLowerCase();
  if (
    normalizedKey.includes('requestbody') ||
    normalizedKey === 'body' ||
    normalizedKey.endsWith('body')
  ) {
    return '[redacted]';
  }

  if (
    typeof value === 'string' &&
    (normalizedKey.includes('url') ||
      normalizedKey.includes('uri') ||
      normalizedKey.includes('endpoint') ||
      normalizedKey.includes('href'))
  ) {
    return summarizeUrlValue(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizePayloadValue(key, item));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(
        ([childKey, child]) => [
          childKey,
          sanitizePayloadValue(childKey, child),
        ],
      ),
    );
  }

  return value;
};

const sanitizeBillingFlowPayload = (
  payload?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!payload) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      sanitizePayloadValue(key, value),
    ]),
  );
};

const persistBillingFlowDiagnosticsContext = (
  context: BillingFlowDiagnosticsContext,
): void => {
  const sessionStorage = getWindowSessionStorage();
  const localStorage = getWindowLocalStorage();

  writeStorage(
    sessionStorage,
    STORAGE_KEYS.correlationId,
    context.correlationId,
  );
  writeStorage(localStorage, STORAGE_KEYS.correlationId, context.correlationId);
  writeStorage(sessionStorage, STORAGE_KEYS.scenarioId, context.scenarioId);
  writeStorage(localStorage, STORAGE_KEYS.scenarioId, context.scenarioId);
  writeStorage(
    sessionStorage,
    STORAGE_KEYS.organizationId,
    context.organizationId,
  );
  writeStorage(
    localStorage,
    STORAGE_KEYS.organizationId,
    context.organizationId,
  );
};

export const getBillingFlowDiagnosticsContext =
  (): BillingFlowDiagnosticsContext => {
    const sessionStorage = getWindowSessionStorage();
    const localStorage = getWindowLocalStorage();
    const searchParams = isBrowser()
      ? new URLSearchParams(globalThis.window.location.search)
      : new URLSearchParams();

    return {
      correlationId:
        searchParams.get(BILLING_FLOW_QUERY_PARAMS.correlationId)?.trim() ||
        readStorage(sessionStorage, STORAGE_KEYS.correlationId) ||
        readStorage(localStorage, STORAGE_KEYS.correlationId) ||
        generateCorrelationId(),
      scenarioId:
        searchParams.get(BILLING_FLOW_QUERY_PARAMS.scenarioId)?.trim() ||
        readStorage(sessionStorage, STORAGE_KEYS.scenarioId) ||
        readStorage(localStorage, STORAGE_KEYS.scenarioId),
      organizationId:
        readStorage(sessionStorage, STORAGE_KEYS.organizationId) ||
        readStorage(localStorage, STORAGE_KEYS.organizationId),
    };
  };

export const ensureBillingFlowDiagnosticsContext = (
  overrides?: Partial<BillingFlowDiagnosticsContext>,
): BillingFlowDiagnosticsContext => {
  const context = {
    ...getBillingFlowDiagnosticsContext(),
    ...overrides,
  };

  persistBillingFlowDiagnosticsContext(context);
  return context;
};

export const setBillingFlowScenarioId = (scenarioId?: string): void => {
  persistBillingFlowDiagnosticsContext({
    ...getBillingFlowDiagnosticsContext(),
    scenarioId,
  });
};

export const setBillingFlowOrganizationId = (organizationId?: string): void => {
  persistBillingFlowDiagnosticsContext({
    ...getBillingFlowDiagnosticsContext(),
    organizationId,
  });
};

export const clearBillingFlowDiagnosticsContext = (): void => {
  const sessionStorage = getWindowSessionStorage();
  const localStorage = getWindowLocalStorage();

  writeStorage(sessionStorage, STORAGE_KEYS.correlationId);
  writeStorage(localStorage, STORAGE_KEYS.correlationId);
  writeStorage(sessionStorage, STORAGE_KEYS.scenarioId);
  writeStorage(localStorage, STORAGE_KEYS.scenarioId);
  writeStorage(sessionStorage, STORAGE_KEYS.organizationId);
  writeStorage(localStorage, STORAGE_KEYS.organizationId);
};

export const appendBillingFlowDiagnosticsToUrl = (
  url: string,
  overrides?: Partial<BillingFlowDiagnosticsContext>,
): string => {
  const context = ensureBillingFlowDiagnosticsContext(overrides);

  try {
    const parsedUrl = new URL(url, globalThis.window.location.origin);
    parsedUrl.searchParams.set(
      BILLING_FLOW_QUERY_PARAMS.correlationId,
      context.correlationId,
    );
    if (context.scenarioId) {
      parsedUrl.searchParams.set(
        BILLING_FLOW_QUERY_PARAMS.scenarioId,
        context.scenarioId,
      );
    }
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

export const billingFlowLog = (
  source: string,
  stage: string,
  payload?: Record<string, unknown>,
): void => {
  if (!shouldEmitBillingFlowDiagnostics()) {
    return;
  }

  const context = ensureBillingFlowDiagnosticsContext();
  const sanitizedPayload = sanitizeBillingFlowPayload(payload);
  const locationSummary =
    isBrowser() && globalThis.window.location
      ? summarizeUrlValue(globalThis.window.location.href)
      : { origin: undefined, pathname: undefined };
  const event = {
    source,
    stage,
    correlationId: context.correlationId,
    scenarioId: context.scenarioId,
    organizationId: context.organizationId,
    locationOrigin: locationSummary.origin,
    locationPathname: locationSummary.pathname,
    ...sanitizedPayload,
  };

  if (!isBrowser()) {
    return;
  }

  (
    globalThis.window as Window & {
      [BILLING_FLOW_EVENTS_KEY]?: Array<Record<string, unknown>>;
    }
  )[BILLING_FLOW_EVENTS_KEY] ??= [];
  (
    globalThis.window as Window & {
      [BILLING_FLOW_EVENTS_KEY]: Array<Record<string, unknown>>;
    }
  )[BILLING_FLOW_EVENTS_KEY].push(event);

  // eslint-disable-next-line no-console
  console.info(`${BILLING_FLOW_CONSOLE_PREFIX} ${source}:${stage}`, event);
  billingFlowTelemetryLogger.info(`${source}:${stage}`, event);
};
